import {EventBus} from '@mintdata-wsp/common/bus/EventBus';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {WorkbenchVisibilityState} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/WorkbenchState';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action} from 'mobx';

export class WorkbenchVisibilityController
{
    public isPropertiesPanelVisible():boolean
    {
        const state = this._getState();

        return state.isPropertiesPanelVisible;
    }

    @action
    public setPropertiesPanelVisibility(visibility:boolean):void
    {
        const state = this._getState();

        state.isPropertiesPanelVisible = visibility;

        this.updateGridRightColumnWidth();
    }

    @action
    public togglePropertiesPanelVisibility():void
    {
        const state = this._getState();

        const toggledValue = !state.isPropertiesPanelVisible;

        this.setPropertiesPanelVisibility(toggledValue);
    }

    public isPagesPanelVisible():boolean
    {
        const state = this._getState();

        return state.isPagesPanelVisible;
    }

    @action
    public setPagesPanelVisibility(visibility:boolean):void
    {
        const state = this._getState();

        state.isPagesPanelVisible = visibility;

        this.updateGridRightColumnWidth();
    }

    @action
    public togglePagesPanelVisibility():void
    {
        const state = this._getState();

        const toggledValue = !state.isPagesPanelVisible;

        this.setPagesPanelVisibility(toggledValue);
    }

    public isSpreadsheetVisible():boolean
    {
        const state = this._getState();

        return state.isSpreadsheetVisible;
    }

    @action
    public setSpreadsheetVisibility(visibility:boolean):void
    {
        const state = this._getState();

        state.isSpreadsheetVisible = visibility;

        EventBus.fire(this, 'spreadsheetPanelWidthChange');
    }

    @action
    public toggleSpreadsheetVisibility():void
    {
        const state = this._getState();

        const toggledValue = !state.isSpreadsheetVisible;

        this.setSpreadsheetVisibility(toggledValue);

        EventBus.fire(this, 'spreadsheetPanelWidthChange');
    }

    public isSurfaceCopyPagesPanelShown():boolean
    {
        const state = this._getState();

        return state.layout.isSurfaceCopyPagesPanelShown;
    }

    @action
    public toggleCopyPagesPanelShown()
    {
        const copyPages = controllers.ui.copyPages;
        const state = this._getState();

        state.layout.isSurfaceCopyPagesPanelShown = !state.layout.isSurfaceCopyPagesPanelShown;

        if( state.layout.isSurfaceCopyPagesPanelShown )
        {
            copyPages.refreshState();
            state.layout.onCopyPagesPanelShow && state.layout.onCopyPagesPanelShow();
        }
        else
            state.layout.onCopyPagesPanelHide && state.layout.onCopyPagesPanelHide();
    }

    @action
    public setSurfaceCopyPagesPanelShown(value:boolean):void
    {
        const state = this._getState();

        if( state.layout.isSurfaceCopyPagesPanelShown === value )
            return;

        state.layout.isSurfaceCopyPagesPanelShown = value;

        if( value )
        {
            controllers.ui.copyPages.refreshState();
            state.layout.onCopyPagesPanelShow && state.layout.onCopyPagesPanelShow();
        }
        else
            state.layout.onCopyPagesPanelHide && state.layout.onCopyPagesPanelHide();
    }

    @action
    public setOnCopyPagesPanelShowCb(callback:() => void):void
    {
        const state = this._getState();

        state.layout.onCopyPagesPanelShow = callback;
    }

    @action
    public setOnCopyPagesPanelHideCb(callback:() => void):void
    {
        const state = this._getState();

        state.layout.onCopyPagesPanelHide = callback;
    }

    @action
    public setOnCopyPagesPanelExpandedPartHeightChange(callback:(deltaPx:number) => void):void
    {
        const state = this._getState();

        state.layout.onCopyPagesPanelExpandedPartHeightChange = callback;
    }

    public isSurfacePagesOutlinePanelCollapsed():boolean
    {
        const state = this._getState();

        return state.layout.isSurfacePagesOutlinePanelCollapsed;
    }

    @action.bound
    public setSurfacePagesOutlinePanelCollapsed(isCollapsed:boolean):void
    {
        const state = this._getState();
        state.layout.isSurfacePagesOutlinePanelCollapsed = isCollapsed;
    }

    public isSurfaceComponentsPanelCollapsed():boolean
    {
        const state = this._getState();

        return state.layout.isSurfaceComponentsPanelCollapsed;
    }

    @action.bound
    public setSurfaceComponentsPanelCollapsed(isCollapsed:boolean):void
    {
        const state = this._getState();
        state.layout.isSurfaceComponentsPanelCollapsed = isCollapsed;
    }

    public isSurfaceBoxesOutlinePanelCollapsed():boolean
    {
        const state = this._getState();

        return state.layout.isSurfaceBoxesOutlinePanelCollapsed;
    }

    @action.bound
    public setSurfaceBoxesOutlinePanelCollapsed(isCollapsed:boolean):void
    {
        const state = this._getState();
        state.layout.isSurfaceBoxesOutlinePanelCollapsed = isCollapsed;
    }

    public getSurfaceComponentsPanelHeight():number
    {
        const state = this._getState();
        return state.layout.surfaceComponentsPanelHeight;
    }

    @action.bound
    public setSurfaceComponentsPanelHeight(height:number):void
    {
        const state = this._getState();
        state.layout.surfaceComponentsPanelHeight = height;
    }

    public getSurfacePagesOutlinePanelHeight():number
    {
        const state = this._getState();
        return state.layout.surfacePagesOutlinePanelHeight;
    }

    @action.bound
    public setSurfacePagesOutlinePanelHeight(height:number):void
    {
        const state = this._getState();
        state.layout.surfacePagesOutlinePanelHeight = height;
    }

    public getSurfaceBoxesOutlinePanelHeight():number
    {
        const state = this._getState();
        return state.layout.surfaceBoxesOutlinePanelHeight;
    }

    @action.bound
    public setSurfaceBoxesOutlinePanelHeight(height:number):void
    {
        const state = this._getState();
        state.layout.surfaceBoxesOutlinePanelHeight = height;
    }

    @action
    public setSurfaceCopyPagesPanelHeight(height:number):void
    {
        const state = this._getState();
        state.layout.surfaceCopyPagesPanelHeight = height;
    }

    public getSurfaceCopyPagesPanelHeight():number
    {
        const state = this._getState();
        return state.layout.surfaceCopyPagesPanelHeight;
    }

    @action
    public setSurfaceCopyPagesPanelExpandedPartHeight(height:number):void
    {
        const state = this._getState();
        const deltaPx:number = height - state.layout.surfaceCopyPagesPanelExpandedPartHeightPx;

        state.layout.surfaceCopyPagesPanelExpandedPartHeightPx = height;
        deltaPx !== 0 && state.layout.onCopyPagesPanelExpandedPartHeightChange(deltaPx);
    }

    public getSurfaceCopyPagesPanelExpandedPartHeight():number
    {
        const state = this._getState();
        return state.layout.surfaceCopyPagesPanelExpandedPartHeightPx;
    }

    public isSpreadsheetFullScreen():boolean
    {
        const state = this._getState();

        return state.isSpreadsheetFullScreen;
    }

    @action
    public setSpreadsheetFullScreen(value:boolean):void
    {
        const {main, mesh, workbook} = controllers.ui.spreadsheets;
        const state = this._getState();

        if( value && !this.isSpreadsheetVisible() )
            this.setSpreadsheetVisibility(true);

        state.isSpreadsheetFullScreen = value;

        if( state.isSpreadsheetFullScreen )
        {
            const activeSheet:AnySheet = main.getActiveSheet();

            if( activeSheet )
            {
                const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(activeSheet.id);

                setTimeout(() => mesh.focusMesh(ctx));
            }
        }

        EventBus.fire(this, 'spreadsheetPanelWidthChange');
    }

    @action
    public toggleSpreadsheetFullScreen():void
    {
        const state = this._getState();

        const toggledValue = !state.isSpreadsheetFullScreen;

        this.setSpreadsheetFullScreen(toggledValue);
    }

    public getGridRightColumnWidth():number
    {
        const state = this._getState();

        return state.layout.gridRightColumnWidth;
    }

    public updateGridRightColumnWidth():void
    {
        requestAnimationFrame(() =>
        {
            const currentWidth = this.getGridRightColumnWidth();
            const maxWidth = this.getMaxRightColumnWidth();

            const width = currentWidth > maxWidth ? maxWidth : currentWidth;

            if( currentWidth !== width )
                this.setGridRightColumnWidth(width);
        });
    }

    public getMaxRightColumnWidth():number
    {
        const state = this._getState();

        const {gridDomRef} = state.layout;

        if( !gridDomRef )
        {
            return 0;
        }

        const gridPanelsTakenSpace = this._getGridPanelsTakenSpace();
        const topBarTakenSpace = this._getTopBarTakenSpace();

        return 100 - Math.max(gridPanelsTakenSpace, topBarTakenSpace);
    }

    private _getGridPanelsTakenSpace():number
    {
        const state = this._getState();

        const {
            gridDomRef,
            gridLeftPanelsDomRef,
            gridRightPanelsDomRef
        } = state.layout;

        if( !gridDomRef || !gridLeftPanelsDomRef || !gridRightPanelsDomRef )
            return 0;

        const gridWidthPx = gridDomRef.getBoundingClientRect().width;
        const onePercentToPx = gridWidthPx / 100;

        const gridLeftPanelsWidthPx = gridLeftPanelsDomRef.getBoundingClientRect().width;
        const gridRightPanelsWidthPx = gridRightPanelsDomRef.getBoundingClientRect().width;
        const gridPanelsWidthPx = gridLeftPanelsWidthPx + gridRightPanelsWidthPx;

        return gridPanelsWidthPx / onePercentToPx;
    }

    private _getTopBarTakenSpace():number
    {
        const state = this._getState();

        const {
            gridDomRef,
            topBarLeftDomRef,
            topBarMiddleDomRef,
            topBarMiddleZoomDomRef,
            topBarRightDomRef
        } = state.layout;

        if( !gridDomRef || !topBarLeftDomRef || !topBarMiddleDomRef || !topBarMiddleZoomDomRef || !topBarRightDomRef )
            return 0;

        const gridWidthPx = gridDomRef.getBoundingClientRect().width;
        const onePercentToPx = gridWidthPx / 100;

        const topBarLeftWidthPx = topBarLeftDomRef.getBoundingClientRect().width;
        const topBarMiddleWidthPx = topBarMiddleDomRef.getBoundingClientRect().width;
        const topBarMiddleZoomWidthPx = topBarMiddleZoomDomRef.getBoundingClientRect().width;
        const topBarRightWidthPx = topBarRightDomRef.getBoundingClientRect().width;

        const topBarTakenWidthPx = topBarLeftWidthPx + topBarRightWidthPx + topBarMiddleWidthPx + topBarMiddleZoomWidthPx;

        return topBarTakenWidthPx / onePercentToPx;
    }

    public getMaxLeftColumnWidthPx():number
    {
        const state = this._getState();

        const {gridDomRef, gridRightPanelsDomRef} = state.layout;

        if( !gridDomRef || !gridRightPanelsDomRef )
        {
            return 0;
        }

        const gridWidthPx:number = gridDomRef.getBoundingClientRect().width;
        const onePercentToPx:number = gridWidthPx / 100;

        const gridRightPanelsTakenSpacePx:number = gridRightPanelsDomRef.getBoundingClientRect().width;
        const gridRightColumnWidthPx = this.getGridRightColumnWidth() * onePercentToPx;

        return gridWidthPx - gridRightPanelsTakenSpacePx - gridRightColumnWidthPx;
    }

    @action
    public setGridRightColumnWidth(width:number):void
    {
        const state = this._getState();

        state.layout.gridRightColumnWidth = width;

        EventBus.fire(this, 'spreadsheetPanelWidthChange');
    }

    public getGridLeftColumnWidthPx():number
    {
        const state = this._getState();

        return state.layout.gridLeftColumnWidthPx;
    }

    @action
    public setGridLeftColumnWidthPx(width:number):void
    {
        const state = this._getState();

        state.layout.gridLeftColumnWidthPx = width;
    }

    public getGridDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.gridDomRef;
    }

    @action
    public setGridDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.gridDomRef = ref;
    }

    public getGridLeftPanelsDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.gridLeftPanelsDomRef;
    }

    @action
    public setGridLeftPanelsDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.gridLeftPanelsDomRef = ref;
    }

    public getGridGlassDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.gridGlassDomRef;
    }

    @action
    public setGridGlassDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.gridGlassDomRef = ref;
    }

    public getGridRightPanelsDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.gridRightPanelsDomRef;
    }

    @action
    public setGridRightPanelsDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.gridRightPanelsDomRef = ref;
    }

    public getTopBarLeftDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.topBarLeftDomRef;
    }

    @action
    public setTopBarLeftDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.topBarLeftDomRef = ref;
    }

    public getTopBarMiddleDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.topBarMiddleDomRef;
    }

    @action
    public setTopBarMiddleDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.topBarMiddleDomRef = ref;
    }

    @action
    public setTopBarMiddleZoomDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.topBarMiddleZoomDomRef = ref;
    }

    public getTopBarRightDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.topBarRightDomRef;
    }

    @action
    public setTopBarRightDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.topBarRightDomRef = ref;
    }

    public getWonderBarDomRef():HTMLElement | null
    {
        const state = this._getState();

        return state.layout.wonderBarDomRef;
    }

    @action
    public setWonderBarDomRef(ref:HTMLElement | null):void
    {
        const state = this._getState();

        state.layout.wonderBarDomRef = ref;
    }

    public getFrameDetailsContainerHeight():number
    {
        const state = this._getState();

        return state.layout.frameDetailsContainerHeight;
    }

    @action
    public setFrameDetailsContainerHeight(height:number):void
    {
        const state = this._getState();

        state.layout.frameDetailsContainerHeight = height;
    }

    private _getState():WorkbenchVisibilityState
    {
        return rootState.ui.visualDesigner.workbench.visibility;
    }
}
